import { useAuth, useOrganization, useUser } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'

/**
 * Resolves the per-tenant assistant name for the active org.
 *
 * Each shop names their own assistant during onboarding (CLAUDE.md
 * "Brand & identity" + memory [AI assistant name is per-tenant]). For
 * dev / GYM Auto / EMSERVICE / any pre-onboarding tenant the column is
 * null and the worker falls back to "Eric" before this hook even sees
 * the value.
 *
 * Backed by `/api/me`, which exposes `assistant_name` alongside the
 * other session fields. The query keys on (user_id, org_id) so an
 * <OrganizationSwitcher> change invalidates the cached name cleanly.
 * staleTime is Infinity — the assistant name doesn't change inside a
 * session.
 *
 * Returns "Eric" while the query is loading so first-paint never
 * shows a blank or "undefined" button label. The real value lands
 * within one round-trip and shadcn's reconciler swaps the text.
 */
export function useAssistantName(): string {
  const { user } = useUser()
  const { organization } = useOrganization()
  const { getToken } = useAuth()

  const query = useQuery<{ assistant_name: string | null | undefined }>({
    queryKey: ['me', 'assistant_name', user?.id, organization?.id],
    enabled: !!user?.id && !!organization?.id,
    staleTime: Infinity,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error('no_token')
      const res = await fetch('/api/me', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('me_failed')
      return (await res.json()) as { assistant_name: string | null | undefined }
    },
  })

  const fromServer = query.data?.assistant_name
  if (typeof fromServer === 'string' && fromServer.trim().length > 0) {
    return fromServer.trim()
  }
  return 'Eric'
}
